import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { sileo } from "sileo";
import { Sexo } from "@/modules/escritorio/types/enums/Genero.enum";
import { capitalizarTexto } from "@/modules/GestionParteV2/utils/partesIA.utils";
import { BUZON_JUDICIAL_NOTICE_DURATION_MS, useUnreadBuzonNotifications } from "./useBuzonJudicial.hook";
import { useUserData } from "./useUserData.hook";
import { Routes } from "../types/enums/Routes.enum";
import { StorageKeys } from "../types/enums/Storage.enum";
import { storage } from "../utils/Const.utils";
import IconBuzonSvgComponent from "../components/IconBuzonSvgComponent/IconBuzonSvgComponent";
import BuzonJudicialNoticeContent from "../components/BuzonJudicialNoticeContent/BuzonJudicialNoticeContent";

const BUZON_JUDICIAL_NOTICE_ID = 'buzon-judicial-post-login-notice';

const readNoticeFlag = (): { expiresAt: number } | null => storage.get(StorageKeys.SHOW_BUZON_JUDICIAL_NOTICE);

/**
 * Muestra una sola vez el aviso de Buzón Judicial tras el post-login (y lo reanuda si se recarga
 * mientras sigue vigente), con el total de pendientes y su desglose por tipo.
 */
export const useBuzonJudicialNotice = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { fullName, idSexo } = useUserData();
    const hasShownBuzonNoticeRef = useRef(false);
    // Misma consulta (y caché) que el badge del Sidebar: total de no leídas + desglose por tipo
    const { data: noLeidas, isSuccess: isNoLeidasSuccess } = useUnreadBuzonNotifications();

    useEffect(() => {
        if (hasShownBuzonNoticeRef.current) return;
        if (!isNoLeidasSuccess) return;

        const notice = readNoticeFlag();
        if (!notice || typeof notice.expiresAt !== 'number') return;

        const remaining = Math.min(BUZON_JUDICIAL_NOTICE_DURATION_MS, notice.expiresAt - Date.now());
        if (remaining <= 0) {
            storage.remove(StorageKeys.SHOW_BUZON_JUDICIAL_NOTICE);
            return;
        }

        const totalPendientes = noLeidas?.count ?? 0;
        if (totalPendientes < 1) return;
        // Si la respuesta no trae el desglose, el aviso muestra solo el total
        const indicadores = noLeidas?.indicadores ?? null;

        hasShownBuzonNoticeRef.current = true;

        const welcome = idSexo === Sexo.Femenino
            ? t('buzonJudicial.postLoginNotice.welcomeFeminine', 'Bienvenida')
            : t('buzonJudicial.postLoginNotice.welcomeMasculine', 'Bienvenido');

        sileo.info({
            id: BUZON_JUDICIAL_NOTICE_ID,
            title: t('buzonJudicial.postLoginNotice.title', 'Aviso Buzón Judicial'),
            description: (
                <BuzonJudicialNoticeContent
                    welcome={welcome}
                    fullName={capitalizarTexto(fullName)}
                    totalPendientes={totalPendientes}
                    indicadores={indicadores}
                />
            ),
            icon: <IconBuzonSvgComponent size={16} />,
            fill: '#FFFFFF',
            button: {
                title: t('buzonJudicial.postLoginNotice.action', 'Abrir Buzón Judicial'),
                onClick: () => {
                    navigate(`/${Routes.BUZON_JUDICIAL}`);
                    sileo.clear();
                    storage.remove(StorageKeys.SHOW_BUZON_JUDICIAL_NOTICE);
                },
            },
            duration: remaining,
            autopilot: { expand: 0, collapse: 120000 },
            styles: {
                title: 'font-bold! text-sm! normal-case! text-[#1f2937]!',
                badge: 'bg-blue-dark/10! text-blue-dark!',
                // Reemplaza el `text-white/75!` global del Toaster (pensado para el fill oscuro)
                description: 'text-[#374151]!',
                button: "w-fit! ml-auto! h-10! px-5! rounded-xl! bg-blue-dark! text-white! font-bold! text-sm! hover:bg-[#0843A0]! after:ml-2 after:content-['→']",
            },
        });
    }, [isNoLeidasSuccess, noLeidas, fullName, idSexo, navigate, t]);
};
